<template>
    <form
        action=""
        class="embryo-development-form"
        :class="{
            discarded: isDiscarded,
            'has-eq-score': showEqScore,
            'fda-friendly': isFdaFriendly
        }"
    >
        <div class="field id">
            <span class="label">{{ $translate('eid') }}:&nbsp;</span>
            <span class="value">{{ well.index + 1 }}</span>
        </div>

        <template v-if="!isDiscarded">
            <div class="field pgt">
                <label :for="`pgt-${well._id}`">{{ $translate('pgt') }}:</label>

                <select :id="`pgt-${well._id}`" v-model="selectedPgta" name="pgt">
                    <option v-for="(pgta, index) in pgtaOptions" :key="index" :value="pgta">
                        {{ pgta ? $translate(pgta) : $translate('na') }}
                    </option>
                </select>
            </div>

            <div v-if="!isFdaFriendly" class="field score">
                <input
                    id="showEqScore"
                    name="showEqScore"
                    type="checkbox"
                    :checked="showEqScore"
                    :disabled="isUS"
                    @change="$emit('eqScoreToggled')"
                />
                <span class="label">{{ $translate('eq_score') }}:&nbsp;</span>
                <span class="value">{{ eqScore }}</span>
            </div>

            <div class="field gardner">
                <label :for="`grade-${well._id}`">{{ $translate('grade') }}:&nbsp;</label>
                <input
                    :id="`grade-${well._id}`"
                    v-model="grade"
                    type="text"
                    name="grade"
                    maxlength="10"
                    placeholder="N/A"
                />
                <label :for="`grade-${well._id}`" class="icon">
                    <i class="fa fa-pencil"></i>
                </label>
            </div>
        </template>

        <div v-if="isDiscarded" class="field discard-reason">
            <select :id="`reason-${well._id}`" v-model="reason" name="reason">
                <option value="no-fert">{{ $translate('no_fertilization') }}</option>
                <option value="abnormal">{{ $translate('abnormal_fertilization') }}</option>
                <option value="arrested">{{ $translate('arrested') }}</option>
                <option value="low-quality">{{ $translate('insufficient_quality') }}</option>
                <option value="degenerated">{{ $translate('degenerated') }}</option>
                <option value="na">{{ $translate('na') }}</option>
            </select>
        </div>

        <div v-if="isDiscarded" class="filler"></div>
        <div v-if="isDiscarded" class="filler"></div>

        <fertilization-type-select :default-type="defaultFertType" class="field" />

        <div class="field fate" :class="[well.status]">
            <span>{{ statusText }}</span>
        </div>
    </form>
</template>

<script>
import { mapGetters } from 'vuex'
import Utils from '../../../../business/utils/Utils'
import UIUtils from '../../../../business/utils/UIUtils'
import { WELL_STATUS } from '../../../../business/factory/DataModel'
import FertilizationTypeSelect from '../FertilizationTypeSelect'
import { PGTA_VALUES } from '../../../../business/const'

export default {
    name: 'EmbryoDevelopmentForm',
    components: { FertilizationTypeSelect },
    props: {
        well: {
            type: Object,
            required: true
        },
        showEqScore: {
            type: Boolean,
            required: true
        }
    },
    data: () => ({
        pgtaOptions: PGTA_VALUES,
        selectedPgta: null,
        grade: '',
        reason: 'na'
    }),
    computed: {
        ...mapGetters({
            isUS: 'common/isUS',
            isFdaFriendly: 'common/isFdaFriendly'
        }),
        isDiscarded() {
            return this.well.status === WELL_STATUS.DISCARDED
        },
        eqScore() {
            return UIUtils.formatScore(this.well.inferenceData.kidScore) || this.$translate('na')
        },
        statusText() {
            const { status } = this.well
            const statusKey = status === 'THAWED' ? 'thawed_transfer' : status
            return this.$translate(statusKey)
        },
        defaultFertType() {
            return Utils.getFertTypeByWell(this.well)
        }
    },
    watch: {
        well: {
            handler(newWellValue) {
                this.selectedPgta = newWellValue.pgta
            },
            deep: true,
            immediate: true
        }
    },
    mounted() {
        this.setDefaultGardner()
    },
    methods: {
        setDefaultGardner() {
            if (this.well.clinicGardner) {
                const { expansion, icm, te } = this.well.clinicGardner

                this.grade = `${expansion}${icm}${te}`
            }
        }
    }
}
</script>

<style lang="scss" scoped>
$active: $light-3;
$passive: $light;

.embryo-development-form {
    display: grid;

    &:not(.discarded) {
        @media screen {
            &:not(.fda-friendly) {
                grid-template-columns: repeat(6, 1fr);
            }
            &.fda-friendly {
                grid-template-columns: repeat(5, 1fr);
            }

            &:not(.has-eq-score) {
                .field.score {
                    .value {
                        color: $passive;
                    }
                }
            }
        }

        @media print {
            &.has-eq-score {
                grid-template-columns: repeat(6, 1fr);
            }
            &:not(.has-eq-score) {
                grid-template-columns: repeat(4, 1fr) 16.6666%;

                .field.score {
                    display: none;
                }
            }

            border: solid 1px $report-color;
            border-bottom: none;

            .field {
                &:not(.fate):not(:first-child) {
                    border-left: solid 1px $report-color;
                }

                &.fertilization-type-select {
                    border-top: solid 15px transparent;
                    border-bottom: solid 15px transparent;
                    border-left: solid 1px black;
                    border-radius: 15px;
                }
            }
        }
    }

    &.discarded {
        @media screen {
            grid-template-columns: 3fr repeat(3, 1fr);

            .filler {
                display: none;
            }
        }
        @media print {
            grid-template-columns: repeat(6, 1fr);
            border: solid 1px $passive;

            .field {
                &:not(.fate):not(:first-child) {
                    border-left: solid 1px $passive;
                }
            }
        }
    }

    .field {
        height: 40px;
        padding: 5px 5px;
        display: flex;
        align-items: center;

        @media screen {
            height: 40px;
            border: solid 1px $active;
            border-radius: 4px;
        }
        @media print {
            height: 45px;
            justify-content: center;

            &:not(.fate):not(:first-child) {
                border-top: solid 15px transparent;
                border-bottom: solid 15px transparent;
                border-radius: 15px;
            }

            .value {
                font-weight: 600;
            }
        }

        &.id {
            @media screen {
                .label {
                    color: $passive;
                }
            }
        }

        &.pgt {
            label {
                margin: 0 5px 0 0;

                @media screen {
                    color: $passive;
                }
            }

            select {
                background-color: transparent;
                border: none;

                option {
                    background-color: $dark-4;
                }

                @media screen {
                    text-align: left;
                    color: $active;
                }
                @media print {
                    text-align: center;
                    font-weight: 600;
                }
            }
        }

        &.score {
            white-space: nowrap;
            input[type='checkbox'] {
                @media screen {
                    position: relative;
                    appearance: none;
                    width: 20px;
                    height: 20px;
                    border: solid 1px $passive;
                    border-radius: 3px;
                    margin-right: 5px;

                    &:not(:disabled) {
                        cursor: pointer;
                    }

                    &:checked:before {
                        content: '';
                        position: absolute;
                        left: 6px;
                        top: 1px;
                        width: 6px;
                        height: 12px;
                        border: solid $active;
                        border-width: 0 1px 1px 0;
                        transform: rotate(45deg);
                        opacity: 1;
                    }
                }
                @media print {
                    display: none;
                }
            }

            .label {
                @media screen {
                    color: $passive;
                }
            }
        }

        &.gardner {
            input {
                width: 50px;
                margin-right: 5px;
                text-align: center;
                border: none;
                background-color: transparent;
            }
            label {
                margin: 0;
            }

            @media screen {
                input {
                    color: $active;

                    &::-webkit-input-placeholder {
                        color: $active;
                    }
                }

                label {
                    color: $passive;
                    cursor: pointer;
                }
            }

            @media print {
                input {
                    font-weight: 600;

                    &::-webkit-input-placeholder {
                        color: black;
                    }
                }

                label i {
                    display: none;
                }
            }
        }

        &.discard-reason {
            select {
                background-color: transparent;
                border: none;

                option {
                    background-color: $dark-4;
                }

                @media screen {
                    text-align: left;
                    color: $active;
                }
                @media print {
                    text-align: center;
                    font-weight: 600;
                }
            }
        }

        &.fate {
            justify-content: center;
            font-weight: 400;
            font-size: 13px;

            @media print {
                border-left: solid 1px $report-color;

                span {
                    padding: 0 0.5rem;
                }
            }

            &.TRANSFERRED {
                @media screen {
                    background-color: $green;
                    border-color: $green;
                }
                @media print {
                    span {
                        color: $active;
                        background-color: $green;
                    }
                }
            }
            &.THAWED {
                @media screen {
                    background-color: $purple;
                    border-color: $purple;
                }
                @media print {
                    span {
                        color: $active;
                        background-color: $purple;
                    }
                }
            }
            &.FROZEN {
                @media screen {
                    background-color: $frozen-color;
                    border-color: $frozen-color;
                }
                @media print {
                    span {
                        color: $active;
                        background-color: $frozen-color;
                    }
                }
            }
            &.PENDING {
                @media screen {
                    background-color: #ffeb00;
                    border-color: #ffeb00;
                    color: $dark;
                }
                @media print {
                    span {
                        background-color: #ffeb00;
                    }
                }
            }
            &.DISCARDED {
                @media screen {
                    background-color: $red;
                    border-color: $red;
                }
                @media print {
                    background-color: #f0f0f0;
                    border-left: solid 1px $passive;
                }
            }
        }
    }

    @media screen {
        column-gap: 10px;
    }
    @media print {
        select {
            appearance: none;
        }
    }
}
</style>
